import {
  PostCallSignal,
  SignalStrength,
} from '../models/post-call-signal.types';

const STRENGTH_VALUES: SignalStrength[] = [
  'Strong',
  'Good',
  'Weak',
  'No signal',
  'Excluded',
];

/**
 * Strength brackets (same as BE; only used if strength is missing):
 * 90–100 Strong, 60–89 Good, 15–59 Weak, 1–14 No signal, 0 Excluded.
 */
export function strengthFromScore(score: number | null | undefined): SignalStrength | undefined {
  if (score == null || !Number.isFinite(Number(score))) {
    return undefined;
  }
  const n = Number(score);
  if (n <= 0) {
    return 'Excluded';
  }
  if (n < 15) {
    return 'No signal';
  }
  if (n < 60) {
    return 'Weak';
  }
  if (n < 90) {
    return 'Good';
  }
  return 'Strong';
}

function asFiniteNumber(value: any): number | null {
  if (value == null || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function normalizeStrength(raw: any, score: number | null): SignalStrength | string | undefined {
  if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim();
    const match = STRENGTH_VALUES.find(
      (s) => s.toLowerCase() === trimmed.toLowerCase()
    );
    return match || trimmed;
  }
  // BE always sets strength; fall back only for incomplete rows.
  return strengthFromScore(score);
}

/**
 * Prefer canonical `task_prompt`; fall back to legacy `ai_task` if still present.
 */
export function resolveSignalTaskPrompt(raw: any): string {
  if (!raw || typeof raw !== 'object') {
    return '';
  }
  const primary = raw.task_prompt != null ? String(raw.task_prompt).trim() : '';
  if (primary) {
    return primary;
  }
  return raw.ai_task != null ? String(raw.ai_task).trim() : '';
}

/**
 * Normalize canonical `signal` object: { score, strength, reason, task_prompt }.
 */
export function normalizePostCallSignal(raw: any): PostCallSignal | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const score = asFiniteNumber(raw.score);
  const reason = String(raw.reason != null ? raw.reason : '').trim();
  const taskPrompt = resolveSignalTaskPrompt(raw);
  const strength = normalizeStrength(raw.strength, score);

  // Empty / null shell — nothing useful to show.
  if (score == null && !strength && !reason && !taskPrompt) {
    return null;
  }

  return {
    score,
    strength,
    reason: reason || undefined,
    task_prompt: taskPrompt || undefined,
  };
}

/**
 * Read signals from a Connected / jobs_status row.
 *
 * Canonical: `call.signal` (singular object or null) from getAllJobsStatus.
 * Also accepts `call.signals[]` if a future BE revision sends multiple.
 */
export function extractPostCallSignals(call: any): PostCallSignal[] {
  if (!call || typeof call !== 'object') {
    return [];
  }

  // Canonical contract path.
  if (call.signal != null) {
    const one = normalizePostCallSignal(call.signal);
    return one ? [one] : [];
  }

  // Optional multi-signal array (not in current BE; keep UI loop-friendly).
  if (Array.isArray(call.signals) && call.signals.length) {
    return call.signals
      .map((raw: any) => normalizePostCallSignal(raw))
      .filter((s: PostCallSignal | null): s is PostCallSignal => !!s);
  }

  return [];
}

/** True when signal has a non-empty task_prompt (Excluded leaves it empty). */
export function signalHasTaskPrompt(signal: PostCallSignal | null | undefined): boolean {
  return !!(signal && signal.task_prompt && String(signal.task_prompt).trim());
}

/** @deprecated Use signalHasTaskPrompt — kept for call-site renames during migration. */
export function signalHasAiTask(signal: PostCallSignal | null | undefined): boolean {
  return signalHasTaskPrompt(signal);
}

/** Short title for create-task dialog from a task_prompt. */
export function titleFromTaskPrompt(taskPrompt: string, fallback = 'Signal follow-up'): string {
  const text = String(taskPrompt || '').trim().replace(/\s+/g, ' ');
  if (!text) {
    return fallback;
  }
  if (text.length <= 72) {
    return text;
  }
  return `${text.slice(0, 69).trim()}…`;
}

/** @deprecated Use titleFromTaskPrompt. */
export function titleFromAiTask(aiTask: string, fallback = 'Signal follow-up'): string {
  return titleFromTaskPrompt(aiTask, fallback);
}

/**
 * Lead / row task_prompt for create-task prefills when no explicit prompt is passed.
 * Prefers signal.task_prompt, then top-level lead/job `task_prompt`.
 */
export function resolveLeadTaskPrompt(callOrLead: any): string {
  if (!callOrLead || typeof callOrLead !== 'object') {
    return '';
  }
  const fromSignal = resolveSignalTaskPrompt(callOrLead.signal);
  if (fromSignal) {
    return fromSignal;
  }
  if (callOrLead.task_prompt != null && String(callOrLead.task_prompt).trim()) {
    return String(callOrLead.task_prompt).trim();
  }
  return '';
}

/** CSS modifier for strength pill (matches Connected terracotta palette). */
export function signalStrengthClass(strength: string | undefined | null): string {
  const key = String(strength || '').trim().toLowerCase();
  if (key === 'strong') {
    return 'signal-strength--strong';
  }
  if (key === 'good') {
    return 'signal-strength--good';
  }
  if (key === 'weak') {
    return 'signal-strength--weak';
  }
  if (key === 'no signal' || key === 'no_signal') {
    return 'signal-strength--none';
  }
  if (key === 'excluded') {
    return 'signal-strength--excluded';
  }
  return 'signal-strength--unknown';
}
